export interface PbxCdrLatestResponse {
	/** Latest 10 outbound calls */
	outbound: PbxCdrLatestCallRecord[]

	/** Latest 10 inbound calls */
	inbound: PbxCdrLatestCallRecord[]

	/** Latest 10 missed calls */
	missed: PbxCdrLatestCallRecord[]
}

/**
 * Represents a call record.
 */
export interface PbxCdrLatestCallRecord {
	/** Unique ID of the call record */
	_id: string

	/** PBX server that handled the call */
	pbx: string

	/** ID of the channel */
	channelId: string

	/** When the call started */
	started: Date

	/** When the call was answered */
	answered?: Date

	/** True if call was answered by the callee. False if not answered or answered by system */
	answeredByCallee: boolean

	/** When the call ended */
	ended: Date

	/** Data about the caller */
	caller: PbxCdrLatestCallerInfo

	/** Data about the callee */
	callee: PbxCdrLatestCalleeInfo

	/** ID of the customer */
	customer: string

	/** Who terminated the call: 'A' for caller, 'B' for callee, or 'S' for system */
	terminatedBy: 'A' | 'B' | 'S'

	/** How the call was terminated */
	terminatedReason: PbxCdrTerminationReason
}

/**
 * Represents caller information.
 */
export interface PbxCdrLatestCallerInfo {
	/** Phone number presented */
	number: string

	/** Name of the caller */
	name?: string

	/** True if the caller is hidden */
	privacy: boolean

	/** Type of caller */
	type: PbxCdrCallerType

	/** If the call was diverted, number of the diverter */
	divertedBy?: string

	/** ID of the extension of the caller if local */
	extension?: string

	/** ID of the SIP phone if caller is local and the call was initiated from a SIP phone */
	sipPhone?: string
}

/**
 * Represents callee information.
 */
export interface PbxCdrLatestCalleeInfo {
	/** Phone number called */
	number: string

	/** Name of the called, if available */
	name?: string

	/** Extension of the called, if callee is local */
	extension?: string
}

/**
 * Enum for caller types.
 */
export enum PbxCdrCallerType {
	SIPPHONE = 'SIPPHONE',
	MOBILE = 'MOBILE',
	EXTERNAL = 'EXTERNAL',
	EXTERNALPHONE = 'EXTERNALPHONE',
	SYSTEM = 'SYSTEM'
}

/**
 * Enum for termination reasons.
 */
export enum PbxCdrTerminationReason {
	CANCEL = 'CANCEL', // No answer, cancelled by caller
	HANGUP = 'HANGUP', // Hangup after answered
	BUSY = 'BUSY', // Callee was busy
	UNREACHABLE = 'UNREACHABLE', // Callee could not be called
	ERROR = 'ERROR', // Call ended due to an error
	BLOCKED = 'BLOCKED', // Call blocked due to suspicion of fraud or blacklisting
	NOT_FOUND = 'NOT_FOUND', // Called number does not exist
	FAILED = 'FAILED' // any of UNREACHABLE, ERROR, BLOCKED, NOT_FOUND.
}

export interface PbxCdrListRequest {
	/** Index of the first item to return, default 0. */
	offset?: number
	/** The number of items to return, default 100, min 1, max 10000. */
	limit?: number
	/** Search query that will match against aNumber, aName, bNumber and bName. */
	filter?: string
	/** Customer the calls belongs to, defaults to users own customer. */
	customer?: string
	/** Employee to show calls for, overridden by extension. */
	employee?: string
	/** Extension to show calls for. */
	extension?: string
	/** Who terminated the call: A for caller, B for callee or S for system. */
	terminatedBy?: 'A' | 'B' | 'S'
	/** How was the call terminated: */
	terminatedReason?: PbxCdrTerminationReason
	/** Start date/time to include calls from, defaults to midnight today. */
	from?: Date
	/** End date/time to include calls until, defaults to from + 24 hours. */
	to?: Date
	/** Direction of calls to include: IN or OUT. Required extension or that user has access level PERSONAL. */
	direction?: 'IN' | 'OUT'
	/** True to also include if a call has been recorded. */
	recording?: boolean
	/** True to only include items with cdrNote */
	cdrNote?: boolean
	/** True to only return calls that was answered by the callee (not by system/voicemail etc). */
	answeredByCallee?: boolean
	/** Data format: JSON, CSV, EXCEL. Defaults to JSON. */
	format?: 'JSON' | 'CSV' | 'EXCEL'
}

export interface PbxCdrListResponse {
	/** Index of the first CDR returned. */
	offset: number
	/** Number of CDR records to return. Note that the actual number of returned items may be lower. */
	limit: number
	/** Number of CDR records that can be returned when offset and limit is not considered. This is to be used for paging through the data. */
	total: number
	/** Array of CDR records, see definition below. */
	cdrs: PbxCdrCall[]
}

export interface PbxCdrCall {
	/** Unique ID of call. */
	_id: string

	/** Hostname of server that handled the call */
	pbx: string

	/** Name of channel on server */
	channelId: string

	/** SIP Call ID, if available */
	callId?: string

	/** Date and time when the call started */
	started: Date

	/** Date and time when the call was answered */
	answered?: Date

	/** True if call was answered by the callee. False if not answered or answered by system */
	answeredByCallee: boolean

	/** Date and time when the call ended */
	ended: Date

	/** Id of customer */
	customer: string

	/** Who terminated the call */
	terminatedBy: PbxCdrTerminatedBy

	/** How was the call terminated */
	terminatedReason: PbxCdrTerminationReason

	/** CDR note as set during the call */
	cdrNote?: string

	/** Caller data */
	caller: {
		/** Callers phone number */
		number: string

		/** Callers name, if available */
		name?: string

		/** Id of extension that made the call, if the call was made on a SIP or MVNO phone */
		extension?: string

		/** Id of the SIP phone the call was made on */
		sipPhone?: string

		/** True if callers number shall be hidden */
		privacy: boolean

		/** Type of caller */
		type: PbxCdrCallerType

		/** If call was diverted, this is the number of the diverter */
		divertedB?: string
	}

	/** Callee data */
	callee: {
		/** Callees phone number */
		number: string

		/** Callees name, if available */
		name?: string

		/** Id of extension that received the call, if it was received on a SIP or MVNO phone */
		extension?: string
	}

	/** List of call recordings - if requested and the call was recorded */
	recording?: {
		/** Id of recording */
		_id: string

		/** Who recorded the call */
		by: PbxCdrRecordingBy

		/** Id of employee the recording belongs to, if by is COMMON */
		employee?: string

		/** Length of recording in seconds */
		length: number

		/** True if follow up has been set */
		followUp: boolean

		/** Follow up note - if any */
		followUpNote: string
	}[]
}

/** Who terminated the call: A for caller, B for callee or S for system */
enum PbxCdrTerminatedBy {
	Caller = 'A',
	Callee = 'B',
	System = 'S'
}

/**
 * Who recorded the call
 */
enum PbxCdrRecordingBy {
	Employee = 'EMPLOYEE',
	Common = 'COMMON'
}
